import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink } from "react-router-dom";
import { createPortal } from 'react-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ShopProvider from './context/ShopContext';
import Cart from './components/Cart';

import Home from "./pages/Home";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import PurchasePage from "./pages/Purchase";
import DashboardPage from './pages/DashboardPage';
import LinkQrPage from './pages/LinkQrPage';
import EditProfilePage from './pages/EditProfilePage';
import QrScanPage from './pages/QrScanPage';
import AdminPage from './pages/AdminPage';
import AdminCompaniesPage from './pages/AdminCompaniesPage';
import AdminAllUsersPage from './pages/AdminAllUsersPage';
import AdminAllQrsPage from './pages/AdminAllQrsPage';
import AdminEditCompanyPage from './pages/AdminEditCompanyPage';
import AdminPrintQrPage from './pages/AdminPrintQrPage';
import AdminUserDetailPage from './pages/AdminUserDetailPage';
import CompanyDashboardPage from './pages/CompanyDashboardPage';
import EditRentalCarPage from './pages/EditRentalCarPage';
import UserProfilePage from './pages/UserProfilePage';
import CompanyProfilePage from './pages/CompanyProfilePage';
import CompanyBillingPage from './pages/CompanyBillingPage';
import CompanyCustomersPage from './pages/CompanyCustomersPage';
import About from './pages/About';
import Contact from './pages/Contact';
import PrivacyKvkk from './pages/PrivacyKvkk';
import VerifyContactPage from './pages/VerifyContactPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import Footer from './components/Footer';
import { Logo } from './components/Logo';


export default function App() {
    return (
        <ShopProvider>
            <AuthProvider>
                <Router>
                    <AppContent />
                    <Cart />
                </Router>
            </AuthProvider>
        </ShopProvider>
    );
}

function AppContent() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    // Lock body scroll when mobile menu is open (prevents scroll-through on Safari iOS)
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isMenuOpen]);

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <header className="w-full bg-white/95 backdrop-blur-xs text-black font-bold shadow-xs sticky top-0 z-50 border-b border-gray-100">
                <div className="container mx-auto px-4 py-3 flex justify-between items-center">
                    <NavLink to="/" className="group flex items-center transition-transform hover:scale-[1.02]">
                        <Logo />
                    </NavLink>
                    <DynamicNavbar isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} />
                </div>
            </header>
            <main className="flex-grow">
                <Routes>
                    {/* Public */}
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy-kvkk" element={<PrivacyKvkk />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/purchase" element={<PurchasePage />} />
                    <Route path="/q/:qrCodeId" element={<QrScanPage />} />

                    {/* All Logged-in Users */}
                    <Route path="/verify-contact" element={<ProtectedRoute><VerifyContactPage /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
                    <Route path="/company-profile" element={<ProtectedRoute companyOnly={true}><CompanyProfilePage /></ProtectedRoute>} />

                    {/* Customer */}
                    <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                    <Route path="/link-qr" element={<ProtectedRoute><LinkQrPage /></ProtectedRoute>} />
                    <Route path="/edit-profile/:profileId" element={<ProtectedRoute><EditProfilePage /></ProtectedRoute>} />

                    {/* Company */}
                    <Route path="/company-dashboard" element={<ProtectedRoute companyOnly={true}><CompanyDashboardPage /></ProtectedRoute>} />
                    <Route path="/company/edit-car/:profileId" element={<ProtectedRoute companyOnly={true}><EditRentalCarPage /></ProtectedRoute>} />
                    <Route path="/company/billing" element={<ProtectedRoute companyOnly={true}><CompanyBillingPage /></ProtectedRoute>} />
                    <Route path="/company/customers" element={<ProtectedRoute companyOnly={true}><CompanyCustomersPage /></ProtectedRoute>} />

                    {/* Admin */}
                    <Route path="/admin" element={<ProtectedRoute adminOnly={true}><AdminPage /></ProtectedRoute>} />
                    <Route path="/admin/companies" element={<ProtectedRoute adminOnly={true}><AdminCompaniesPage /></ProtectedRoute>} />
                    <Route path="/admin/edit-company/:userId" element={<ProtectedRoute adminOnly={true}><AdminEditCompanyPage /></ProtectedRoute>} />
                    <Route path="/admin/users" element={<ProtectedRoute adminOnly={true}><AdminAllUsersPage /></ProtectedRoute>} />
                    <Route path="/admin/user/:userId" element={<ProtectedRoute adminOnly={true}><AdminUserDetailPage /></ProtectedRoute>} />
                    <Route path="/admin/qrcodes" element={<ProtectedRoute adminOnly={true}><AdminAllQrsPage /></ProtectedRoute>} />
                    <Route path="/admin/print-qr" element={<ProtectedRoute adminOnly={true}><AdminPrintQrPage /></ProtectedRoute>} />
                </Routes>
            </main>
            <Footer />
        </div>
    );
}

// --- NAVBAR COMPONENT (Fixed: single useAuth call, no race condition) ---
const DynamicNavbar = ({ isMenuOpen, toggleMenu }) => {
    // Single source of truth – pass data down to NavLinks as props
    const { currentUser, userData, loading, logout } = useAuth();

    const handleLogout = async () => {
        try {
            await logout();
            if (isMenuOpen) toggleMenu();
        } catch (error) {
            console.error("Failed to log out:", error);
        }
    };

    const mobileMenu = (
        <div
            className={`md:hidden fixed inset-0 z-[100] bg-black/50 transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            onClick={toggleMenu}
        >
            <div
                className={`absolute right-0 top-0 h-full w-72 bg-white shadow-2xl p-6 pt-20 transform transition-transform duration-300 ease-in-out ${isMenuOpen ? "translate-x-0" : "translate-x-full"}`}
                style={{ WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden' }}
                onClick={e => e.stopPropagation()}
            >
                <button
                    className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    onClick={toggleMenu}
                >
                    <span className="material-icons text-2xl">close</span>
                </button>
                <nav className="flex flex-col space-y-2 mt-4">
                    <NavLinks
                        currentUser={currentUser}
                        userData={userData}
                        loading={loading}
                        handleLogout={handleLogout}
                        isMobile={true}
                        onAction={toggleMenu}
                    />
                </nav>
            </div>
        </div>
    );

    return (
        <>
            {/* Hamburger button (always shows menu since the close button is inside the popup now) */}
            <button className="md:hidden z-50 p-2 rounded-lg hover:bg-gray-100 transition-colors" onClick={toggleMenu}>
                <span className="material-icons text-2xl">menu</span>
            </button>

            {/* Desktop nav */}
            <nav className="hidden md:flex space-x-1 items-center">
                <NavLinks
                    currentUser={currentUser}
                    userData={userData}
                    loading={loading}
                    handleLogout={handleLogout}
                    isMobile={false}
                    onAction={() => { }}
                />
            </nav>

            {/* Mobile overlay */}
            {typeof document !== 'undefined' ? createPortal(mobileMenu, document.body) : null}
        </>
    );
};

// NavLinks receives all state as props (no race condition)
const NavLinks = ({ currentUser, userData, loading, handleLogout, isMobile, onAction }) => {
    const role = userData?.role;

    const linkClass = isMobile
        ? "block px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-black transition-colors font-medium"
        : "px-3 py-2 rounded-lg text-gray-600 hover:text-black hover:bg-gray-100 transition-colors text-sm font-medium";

    const activeLinkClass = isMobile
        ? "block px-4 py-3 rounded-lg text-black bg-blue-50 font-bold"
        : "px-3 py-2 rounded-lg text-black bg-blue-50 font-bold text-sm";

    const logoutBtnClass = isMobile
        ? "w-full text-left px-4 py-3 rounded-lg text-red-600 font-bold hover:bg-red-50 transition-colors"
        : "ml-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 hover:shadow-md text-sm font-semibold";

    return (
        <>
            {/* Common Links */}
            <NavLink to="/" end className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Ana Sayfa</NavLink>
            <NavLink to="/purchase" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Satın Al</NavLink>

            {isMobile && <hr className="border-gray-100 my-2" />}

            {/* Show nothing while first-load */}
            {loading && null}

            {/* Logged In Links */}
            {!loading && currentUser && (
                <>
                    {/* Role-specific links */}
                    {role === 'customer' && (
                        <>
                            <NavLink to="/dashboard" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Panelim</NavLink>
                            <NavLink to="/profile" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Profilim</NavLink>
                        </>
                    )}
                    {role === 'company' && (
                        <>
                            <NavLink to="/company-dashboard" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Filo Panelim</NavLink>
                            <NavLink to="/company/billing" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Faturalar</NavLink>
                            <NavLink to="/company/customers" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Müşterilerim</NavLink>
                            <NavLink to="/company-profile" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Şirket Profili</NavLink>
                        </>
                    )}
                    {role === 'admin' && (
                        <>
                            <NavLink to="/admin" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Admin Paneli</NavLink>
                            <NavLink to="/profile" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Profilim</NavLink>
                        </>
                    )}
                    {/* Fallback: logged in but role unknown (e.g. Firestore doc missing) */}
                    {!role && (
                        <>
                            <NavLink to="/admin" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Admin Paneli</NavLink>
                            <NavLink to="/profile" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Profilim</NavLink>
                            <NavLink to="/dashboard" className={({ isActive }) => isActive ? activeLinkClass : linkClass} onClick={onAction}>Panelim</NavLink>
                        </>
                    )}

                    {isMobile && <hr className="border-gray-100 my-2" />}
                    <button
                        onClick={() => { handleLogout(); onAction(); }}
                        className={logoutBtnClass}
                    >
                        Çıkış Yap
                    </button>
                </>
            )}

            {/* Logged Out Links */}
            {!loading && !currentUser && (
                <>
                    <NavLink to="/register" className={linkClass} onClick={onAction}>Kayıt Ol</NavLink>
                    <NavLink
                        to="/login"
                        className={isMobile
                            ? "block px-4 py-3 rounded-lg text-black font-medium hover:bg-gray-50"
                            : "ml-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all duration-200 hover:shadow-md text-sm font-semibold"
                        }
                        onClick={onAction}
                    >
                        Giriş
                    </NavLink>
                </>
            )}
        </>
    );
};